import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Stack
} from '@mui/material';
import SkeletonLoader from '../common/SkeletonLoader';
import {
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import {
  obterRelatorioUsuario,
  RelatorioUsuarioResponse,
  formatarPorcentagem,
  formatarTempoConclusao,
  obterCorTaxaConclusao,
  FiltrosRelatorio
} from '../../services/relatorioService';
import FiltrosRelatorioComponent from './FiltrosRelatorio';

const RelatorioUsuario: React.FC = () => {
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string>('');
  const [relatorio, setRelatorio] = useState<RelatorioUsuarioResponse | null>(null);
  const [filtrosAtivos, setFiltrosAtivos] = useState<FiltrosRelatorio>({});

  const carregarRelatorio = async (periodo?: { dataInicio: string; dataFim: string }, filtros?: FiltrosRelatorio) => {
    setCarregando(true);
    setErro('');

    try {
      const filtrosParaUsar = filtros || filtrosAtivos;
      
      // Usar datas dos filtros se disponíveis, senão usar período
      let dataInicio: string | undefined;
      let dataFim: string | undefined;

      if (filtrosParaUsar.dataInicio || filtrosParaUsar.dataFim) {
        dataInicio = filtrosParaUsar.dataInicio;
        dataFim = filtrosParaUsar.dataFim;
      } else if (periodo) {
        dataInicio = periodo.dataInicio;
        dataFim = periodo.dataFim;
      }

      const dados = await obterRelatorioUsuario(dataInicio, dataFim, filtrosParaUsar);
      setRelatorio(dados);
    } catch (error: any) {
      setErro(error.message || 'Erro ao carregar relatório');
      console.error('Erro ao carregar relatório:', error);
    } finally {
      setCarregando(false);
    }
  };

  const handleFiltrosChange = (novosFiltros: FiltrosRelatorio) => {
    setFiltrosAtivos(novosFiltros);
    carregarRelatorio(undefined, novosFiltros);
  };

  useEffect(() => {
    carregarRelatorio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (carregando) {
    return <SkeletonLoader />;
  }

  if (erro) {
    return <Alert severity="error" sx={{ mt: 2 }}>{erro}</Alert>;
  }

  if (!relatorio) {
    return <Alert severity="info" sx={{ mt: 2 }}>Nenhum dado disponível</Alert>;
  }

  const { resumo, porStatus, porPrioridade, tendencias } = relatorio;

  return (
    <Box sx={{ p: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Meu Relatório Pessoal
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {relatorio.periodo.dataInicio && relatorio.periodo.dataFim
            ? `${relatorio.periodo.dataInicio} até ${relatorio.periodo.dataFim}`
            : 'Todos os períodos'}
        </Typography>
      </Box>

      {/* Filtros Avançados */}
      <FiltrosRelatorioComponent
        onFiltrosChange={handleFiltrosChange}
        tipoRelatorio="usuario"
      />

      {/* Cards de Resumo */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 3 }} flexWrap="wrap">
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Total de Tarefas</Typography>
              <Typography variant="h4">{resumo.totalTarefas}</Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Concluídas</Typography>
              <Typography variant="h4" color="success.main">{resumo.tarefasConcluidas}</Typography>
              <Typography variant="caption" color="text.secondary">
                {formatarPorcentagem(resumo.taxaConclusao)} de conclusão
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Pendentes</Typography>
              <Typography variant="h4" color="warning.main">{resumo.tarefasPendentes}</Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Atrasadas</Typography>
              <Typography variant="h4" color="error.main">{resumo.tarefasAtrasadas}</Typography>
            </CardContent>
          </Card>
        </Box>
      </Stack>

      {/* Taxa de Conclusão */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 3 }}>
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 65%' } }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Taxa de Conclusão</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={resumo.taxaConclusao}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: obterCorTaxaConclusao(resumo.taxaConclusao)
                      }
                    }}
                  />
                </Box>
                <Typography variant="h6" sx={{ color: obterCorTaxaConclusao(resumo.taxaConclusao) }}>
                  {formatarPorcentagem(resumo.taxaConclusao)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 35%' } }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Tempo Médio</Typography>
              <Typography variant="h4" color="primary">
                {formatarTempoConclusao(resumo.tempoMedioConclusao)}
              </Typography>
              <Typography variant="caption" color="text.secondary">Para concluir tarefas</Typography>
            </CardContent>
          </Card>
        </Box>
      </Stack>

      {/* Tarefas por Status e Prioridade */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Minhas Tarefas por Status</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Quantidade</TableCell>
                      <TableCell align="right">Porcentagem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {porStatus.map((item) => {
                      const porcentagem = resumo.totalTarefas > 0 
                        ? Math.round((item.quantidade / resumo.totalTarefas) * 100) 
                        : 0;
                      return (
                        <TableRow key={item.status}>
                          <TableCell>{item.status}</TableCell>
                          <TableCell align="right">
                            <Chip label={item.quantidade} size="small" />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="text.secondary">
                              {formatarPorcentagem(porcentagem)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Minhas Tarefas por Prioridade</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Prioridade</TableCell>
                      <TableCell align="right">Quantidade</TableCell>
                      <TableCell align="right">Porcentagem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {porPrioridade.map((item) => {
                      const porcentagem = resumo.totalTarefas > 0 
                        ? Math.round((item.quantidade / resumo.totalTarefas) * 100) 
                        : 0;
                      return (
                        <TableRow key={item.prioridade}>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{item.prioridade}</TableCell>
                          <TableCell align="right">
                            <Chip
                              label={item.quantidade}
                              size="small"
                              color={
                                item.prioridade === 'urgente' ? 'error' :
                                item.prioridade === 'alta' ? 'warning' :
                                item.prioridade === 'media' ? 'info' : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="text.secondary">
                              {formatarPorcentagem(porcentagem)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      </Stack>

      {/* Tendências */}
      {tendencias.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Minhas Tendências (Últimos 7 dias)</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell align="right">Recebidas</TableCell>
                    <TableCell align="right">Concluídas</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tendencias.map((item) => {
                    const saldo = item.concluidas - item.recebidas;
                    return (
                      <TableRow key={item.data}>
                        <TableCell>{new Date(item.data).toLocaleDateString('pt-BR')}</TableCell>
                        <TableCell align="right">{item.recebidas}</TableCell>
                        <TableCell align="right">{item.concluidas}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            {saldo > 0 ? (
                              <>
                                <TrendingUpIcon fontSize="small" color="success" />
                                <Typography variant="body2" color="success.main">+{saldo}</Typography>
                              </>
                            ) : saldo < 0 ? (
                              <>
                                <TrendingDownIcon fontSize="small" color="error" />
                                <Typography variant="body2" color="error.main">{saldo}</Typography>
                              </>
                            ) : (
                              <Typography variant="body2" color="text.secondary">0</Typography>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default RelatorioUsuario;
